import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IngredientAutocomplete from './IngredientAutocomplete';

function RecipeEditor({ recipe, onSave, onDelete, onCancel, pantryList }) {
  const { t } = useTranslation();
  const [name, setName] = useState(recipe ? recipe.name : '');
  const [ingredients, setIngredients] = useState(
    recipe ? recipe.ingredients.map((ing) => ({ ...ing })) : [{ name: '', quantity: 1 }]
  );
  const [notes, setNotes] = useState(recipe ? (recipe.notes || '') : '');
  const [servings, setServings] = useState(recipe ? (recipe.servings || '') : '');
  const qtyRefs = useRef([]);

  const handleIngredientChange = (index, field, value) => {
    const updated = ingredients.map((ing, i) =>
      i === index
        ? { ...ing, [field]: value }
        : ing
    );
    setIngredients(updated);
  };

  const addIngredient = () => {
    setIngredients([...ingredients, { name: '', quantity: 1 }]);
  };

  const removeIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      id: recipe?.id,
      name: name.trim(),
      ingredients: ingredients
        .filter((ing) => ing.name.trim())
        .map((ing) => ({ ...ing, quantity: parseFloat(ing.quantity) || 0 })),
      notes,
      servings: servings ? parseInt(servings, 10) || null : null,
    });
  };

  return (
    <form className="recipe-editor" onSubmit={handleSubmit}>
      <input
        className="recipe-name-input"
        type="text"
        placeholder={t('recipes.namePlaceholder')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <textarea
        className="recipe-notes-input"
        placeholder={t('recipes.notesPlaceholder')}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
      />
      <div className="recipe-servings-row">
        <label className="recipe-servings-label">{t('recipes.servings')}</label>
        <input
          className="recipe-servings-input"
          type="number"
          inputMode="numeric"
          min="1"
          max="100"
          placeholder={t('recipes.servingsPlaceholder')}
          value={servings}
          onChange={(e) => setServings(e.target.value)}
        />
      </div>
      <div className="recipe-ingredients-list">
        {ingredients.map((ing, index) => (
          <div key={index} className="inline-ingredient-row">
            <IngredientAutocomplete
              value={ing.name}
              pantryList={pantryList}
              placeholder={t('recipes.ingredient')}
              onChange={(val) => handleIngredientChange(index, 'name', val)}
              onAccept={() => {
                const el = qtyRefs.current[index];
                if (el) setTimeout(() => el.focus(), 0);
              }}
              t={t}
            />
            <input
              ref={(el) => (qtyRefs.current[index] = el)}
              className="inline-qty"
              type="text"
              inputMode="decimal"
              value={ing.quantity}
              onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
            />
            <button
              type="button"
              className="inline-remove-btn"
              onClick={() => removeIngredient(index)}
              title={t('recipes.removeTitle')}
            >
              ✕
            </button>
          </div>
        ))}
        <button type="button" className="inline-add-btn" onClick={addIngredient}>
          {t('recipes.addIngredient')}
        </button>
      </div>
      <div className="recipe-editor-actions">
        <button type="submit" className="recipe-save-btn">
          {recipe ? t('recipes.update') : t('recipes.addRecipe')}
        </button>
        {recipe && onDelete && (
          <button type="button" className="recipe-delete-btn" onClick={() => onDelete(recipe.id)}>
            {t('recipes.delete')}
          </button>
        )}
        {onCancel && (
          <button type="button" className="recipe-cancel-btn" onClick={onCancel}>
            {t('recipes.cancel')}
          </button>
        )}
      </div>
    </form>
  );
}

const DAYS_LIST = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const MEAL_TYPES_LIST = ['lunch', 'dinner'];

function QuickSlotPicker({ recipe, onSlot, onClose, t }) {
  const [servings, setServings] = useState(recipe.servings ? String(recipe.servings) : '');

  const scaleAndSlot = (day, mt) => {
    const target = parseInt(servings, 10);
    if (recipe.servings && target && target !== recipe.servings) {
      const scale = target / recipe.servings;
      const scaled = {
        ...recipe,
        ingredients: recipe.ingredients.map((ing) => ({
          ...ing,
          quantity: Math.round(ing.quantity * scale * 100) / 100,
        })),
      };
      onSlot(day, mt, scaled);
    } else {
      onSlot(day, mt, recipe);
    }
  };

  return (
    <div className="note-dialog-overlay" onClick={onClose}>
      <div className="quick-slot-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>{t('recipes.planThis')}: {recipe.name}</h3>
        {recipe.servings && (
          <div className="quick-slot-servings">
            <span className="servings-dialog-info">
              {t('week.recipeServingsInfo', { count: recipe.servings })}
            </span>
            <div className="recipe-servings-row">
              <label className="recipe-servings-label">{t('week.cookingForHow')}</label>
              <input
                className="recipe-servings-input"
                type="number"
                inputMode="numeric"
                min="1"
                max="100"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
              />
            </div>
          </div>
        )}
        <div className="quick-slot-grid">
          {DAYS_LIST.map((day) => (
            <div key={day} className="quick-slot-row">
              <span className="quick-slot-day">{t(`week.${day}`, day)}</span>
              {MEAL_TYPES_LIST.map((mt) => (
                <button
                  key={mt}
                  className="quick-slot-btn"
                  onClick={() => scaleAndSlot(day, mt)}
                >
                  {mt === 'lunch' ? '☀️' : '🌙'}
                </button>
              ))}
            </div>
          ))}
        </div>
        <button type="button" className="recipe-cancel-btn" onClick={onClose}>
          {t('recipes.cancel')}
        </button>
      </div>
    </div>
  );
}

function Recipes({ recipes, pantryItems, onAdd, onUpdate, onDelete, onQuickSlot }) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [quickSlotRecipe, setQuickSlotRecipe] = useState(null);

  const pantryList = useMemo(() => (pantryItems || []).map((p) => p.name), [pantryItems]);

  const handleSave = async (recipe) => {
    if (recipe.id) {
      await onUpdate(recipe);
      setEditingId(null);
    } else {
      await onAdd(recipe);
      setShowNew(false);
    }
  };

  const handleDelete = async (id) => {
    await onDelete(id);
    setEditingId(null);
  };

  const handleToggleFavorite = useCallback(async (e, recipe) => {
    e.stopPropagation();
    await onUpdate({ ...recipe, favorite: !recipe.favorite });
  }, [onUpdate]);

  const handleQuickSlot = useCallback((day, mealType, recipe) => {
    setQuickSlotRecipe(null);
    if (onQuickSlot) onQuickSlot(day, mealType, recipe);
  }, [onQuickSlot]);

  const sortedRecipes = useMemo(() =>
    recipes.slice().sort((a, b) => {
      if (a.favorite && !b.favorite) return -1;
      if (!a.favorite && b.favorite) return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    }),
    [recipes]
  );

  return (
    <div className="recipes-section">
      <div className="recipes-header">
        <h2>{t('recipes.title')}</h2>
        {!showNew && (
          <button className="recipe-new-btn" onClick={() => { setShowNew(true); setEditingId(null); }}>
            {t('recipes.newRecipe')}
          </button>
        )}
      </div>
      {showNew && (
        <RecipeEditor
          recipe={null}
          onSave={handleSave}
          onCancel={() => setShowNew(false)}
          pantryList={pantryList}
        />
      )}
      <div className="recipes-list">
        {sortedRecipes.map((recipe) =>
            editingId === recipe.id ? (
              <RecipeEditor
                key={recipe.id}
                recipe={recipe}
                onSave={handleSave}
                onDelete={handleDelete}
                onCancel={() => setEditingId(null)}
                pantryList={pantryList}
              />
            ) : (
              <div key={recipe.id} className="recipe-card" onClick={() => { setEditingId(recipe.id); setShowNew(false); }}>
                <div className="recipe-card-header">
                  <div className="recipe-card-name">{recipe.name}</div>
                  <div className="recipe-card-actions">
                    <button
                      type="button"
                      className={`recipe-fav-btn${recipe.favorite ? ' active' : ''}`}
                      onClick={(e) => handleToggleFavorite(e, recipe)}
                      title={recipe.favorite ? t('recipes.unfavorite') : t('recipes.favorite')}
                    >
                      {recipe.favorite ? '★' : '☆'}
                    </button>
                    {onQuickSlot && (
                      <button
                        type="button"
                        className="recipe-plan-btn"
                        onClick={(e) => { e.stopPropagation(); setQuickSlotRecipe(recipe); }}
                        title={t('recipes.planThis')}
                      >
                        {t('recipes.planThis')}
                      </button>
                    )}
                  </div>
                </div>
                {recipe.notes && (
                  <div className="recipe-card-notes">{recipe.notes}</div>
                )}
                {recipe.servings && (
                  <div className="recipe-card-servings">{t('recipes.servingsDisplay', { count: recipe.servings })}</div>
                )}
                <div className="recipe-card-ingredients">
                  {recipe.ingredients.map((ing, i) => (
                    <span key={i} className="ingredient-chip">
                      <span className="qty">{ing.quantity}</span> {ing.name}
                    </span>
                  ))}
                </div>
              </div>
            )
          )}
      </div>
      {quickSlotRecipe && (
        <QuickSlotPicker
          recipe={quickSlotRecipe}
          onSlot={handleQuickSlot}
          onClose={() => setQuickSlotRecipe(null)}
          t={t}
        />
      )}
    </div>
  );
}

export default Recipes;
